import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiCheckCircle, FiStar, FiCalendar, FiBriefcase } from 'react-icons/fi';
import { API_BASE_URL as API_BASE } from '../services/api';

const SkeletonCard = () => (
  <div className="builder-card builder-skeleton">
    <div className="builder-card-top">
      <div className="builder-logo-wrap skeleton-pulse" style={{ width: 56, height: 56, borderRadius: 14 }} />
      <div style={{ flex: 1 }}>
        <div className="skeleton-pulse" style={{ width: '70%', height: 16, borderRadius: 6, marginBottom: 8 }} />
        <div className="skeleton-pulse" style={{ width: '40%', height: 12, borderRadius: 6 }} />
      </div>
    </div>
    <div className="skeleton-pulse" style={{ width: '100%', height: 12, borderRadius: 6, marginBottom: 8 }} />
    <div className="skeleton-pulse" style={{ width: '80%', height: 12, borderRadius: 6, marginBottom: 20 }} />
    <div className="builder-stats">
      <div className="builder-stat">
        <div className="skeleton-pulse" style={{ width: 28, height: 18, borderRadius: 6, margin: '0 auto 4px' }} />
        <div className="skeleton-pulse" style={{ width: 40, height: 10, borderRadius: 4, margin: '0 auto' }} />
      </div>
      <div className="builder-stat">
        <div className="skeleton-pulse" style={{ width: 28, height: 18, borderRadius: 6, margin: '0 auto 4px' }} />
        <div className="skeleton-pulse" style={{ width: 40, height: 10, borderRadius: 4, margin: '0 auto' }} />
      </div>
      <div className="builder-stat">
        <div className="skeleton-pulse" style={{ width: 28, height: 18, borderRadius: 6, margin: '0 auto 4px' }} />
        <div className="skeleton-pulse" style={{ width: 40, height: 10, borderRadius: 4, margin: '0 auto' }} />
      </div>
    </div>
  </div>
);

const Builders = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [builders, setBuilders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/builders/`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        const mapped = list.map((b) => ({
          id: b.id,
          name: b.name,
          description: b.description || '',
          projects: b.projects_count || 0,
          founded_year: b.founded_year || null,
          rating: b.avg_rating || null,
          reviews_count: b.reviews_count || 0,
          is_verified: !!b.is_verified,
          logo: b.logo,
        }));
        setBuilders(mapped);
      })
      .catch(() => setBuilders([]))
      .finally(() => setLoading(false));
  }, []);

  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  const card = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  const ratingStars = (rating) => {
    const filled = rating ? Math.round(rating) : 0;
    return Array.from({ length: 5 }).map((_, i) => (
      <FiStar key={i} fill={i < filled ? 'currentColor' : 'none'} />
    ));
  };

  return (
    <section className="builders-section section-container" id="builders" ref={ref}>
      <div className="builders-header">
        <span className="section-tag">Hamkorlar</span>
        <h2 className="section-title">
          Ishonchli <span className="gold-text">quruvchilar</span>
        </h2>
        <p className="section-desc">
          Barcha qurilish kompaniyalari tekshirilgan va tasdiqlangan. Sifat kafolatlangan.
        </p>
      </div>

      {/* Loading skeletons */}
      {loading && (
        <div className="builders-grid">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Empty state */}
      {!loading && builders.length === 0 && (
        <div className="builders-empty">
          <FiBriefcase className="builders-empty-icon" />
          <p>Tez orada qurilish kompaniyalari ro'yxati shu yerda paydo bo'ladi.</p>
        </div>
      )}

      {/* Builder cards */}
      {!loading && builders.length > 0 && (
        <motion.div
          className="builders-grid"
          variants={container}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {builders.map((b) => (
            <motion.div className="builder-card" key={b.id} variants={card}>
              <div className="builder-card-top">
                {b.logo ? (
                  <img src={b.logo} alt={b.name} className="builder-logo" />
                ) : (
                  <div className="builder-logo-placeholder">
                    {b.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="builder-card-title">
                  <div className="builder-card-head">
                    <span className="builder-name">{b.name}</span>
                    {b.is_verified && (
                      <span className="builder-verified" title="Tasdiqlangan">
                        <FiCheckCircle />
                      </span>
                    )}
                  </div>
                  {b.founded_year && (
                    <span className="builder-founded">
                      <FiCalendar />
                      {b.founded_year}-yildan beri
                    </span>
                  )}
                </div>
              </div>

              {b.description && (
                <p className="builder-description">{b.description}</p>
              )}

              <div className="builder-stats">
                <div className="builder-stat">
                  <div className="builder-stat-value">{b.projects}</div>
                  <div className="builder-stat-label">Loyiha</div>
                </div>
                <div className="builder-stat">
                  <div className="builder-stat-value">{b.reviews_count}</div>
                  <div className="builder-stat-label">Sharhlar</div>
                </div>
                <div className="builder-stat">
                  <div className="builder-stat-value">{b.rating ? b.rating.toFixed(1) : '—'}</div>
                  <div className="builder-stat-label">Reyting</div>
                </div>
              </div>

              <div className="builder-rating">
                <div className="builder-rating-stars">
                  {ratingStars(b.rating)}
                </div>
                {b.rating && (
                  <span className="builder-rating-value">{b.rating.toFixed(1)}</span>
                )}
                {!b.rating && (
                  <span className="builder-rating-value builder-rating-new">Yangi</span>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </section>
  );
};

export default Builders;
